import { encrypt, decrypt } from './encryption';

// Keys that should NOT be encrypted
const NON_ENCRYPTED_KEYS = [
  'accessToken',
  'refreshToken'
];

/**
 * Secure localStorage wrapper that encrypts all data except access tokens
 */
class SecureLocalStorage {
  /**
   * Set item in localStorage with encryption (except for access tokens)
   */
  setItem(key: string, value: string): void {
    try {
      if (NON_ENCRYPTED_KEYS.includes(key)) {
        // Don't encrypt access tokens
        localStorage.setItem(key, value);
      } else {
        // Encrypt all other data
        const encryptedValue = encrypt(value);
        localStorage.setItem(key, encryptedValue);
      }
    } catch (error) {
      console.error('Error setting localStorage item:', error);
      // Fallback to unencrypted storage if encryption fails
      localStorage.setItem(key, value);
    }
  }

  /**
   * Get item from localStorage with decryption (except for access tokens)
   */
  getItem(key: string): string | null {
    try {
      const value = localStorage.getItem(key);
      if (!value) return null;

      if (NON_ENCRYPTED_KEYS.includes(key)) {
        // Don't decrypt access tokens
        return value;
      } else {
        // Decrypt all other data
        return decrypt(value);
      }
    } catch (error) {
      console.error('Error getting localStorage item:', error);
      // Return the raw value if decryption fails
      return localStorage.getItem(key);
    }
  }

  /**
   * Remove item from localStorage
   */
  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing localStorage item:', error);
    }
  }

  /**
   * Clear all items from localStorage
   */
  clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  }

  /**
   * Get all keys from localStorage
   */
  keys(): string[] {
    try {
      return Object.keys(localStorage);
    } catch (error) {
      console.error('Error getting localStorage keys:', error);
      return [];
    }
  }

  /**
   * Check if a key exists in localStorage
   */
  hasItem(key: string): boolean {
    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      console.error('Error checking localStorage item:', error);
      return false;
    }
  }
}

// Create a singleton instance
export const secureLocalStorage = new SecureLocalStorage();

// Export convenience functions
export const setSecureItem = (key: string, value: string) => secureLocalStorage.setItem(key, value);
export const getSecureItem = (key: string) => secureLocalStorage.getItem(key);
export const removeSecureItem = (key: string) => secureLocalStorage.removeItem(key);
export const clearSecureStorage = () => secureLocalStorage.clear();
export const getSecureKeys = () => secureLocalStorage.keys();
export const hasSecureItem = (key: string) => secureLocalStorage.hasItem(key);

// JSON convenience functions
export const setSecureJSON = (key: string, value: any) => {
  secureLocalStorage.setItem(key, JSON.stringify(value));
};

export const getSecureJSON = (key: string) => {
  const value = secureLocalStorage.getItem(key);
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    console.error('Error parsing JSON from localStorage:', error);
    return null;
  }
}; 